/**
 * One-shot localStorage key migration for the Pipeline Pilot → Teamlane rebrand.
 *
 * The brand rename moved every persisted client preference off the old
 * brand-bearing prefixes onto the Teamlane equivalents:
 *
 *   pp:…            → tl:…
 *   pipelinepilot.… → teamlane.…
 *   pipelinepilot:… → teamlane:…
 *   pipeline_pilot_…→ teamlane_…
 *
 * Without a migration, existing users would silently lose saved table layouts,
 * filters, dashboard section config, etc. This copies each legacy key to its new
 * name (only when the new key isn't already set) and removes the old one. It is
 * idempotent — once run, no legacy keys remain, so re-running is a no-op.
 *
 * Run once at startup, before any hook reads localStorage.
 */

/** Returns the rebranded key name, or null if the key isn't a legacy brand key. */
export function rebrandStorageKey(key: string): string | null {
  if (key.startsWith('pp:')) return `tl:${key.slice(3)}`;
  if (key.startsWith('pipelinepilot')) return `teamlane${key.slice('pipelinepilot'.length)}`;
  if (key.startsWith('pipeline_pilot')) return `teamlane${key.slice('pipeline_pilot'.length)}`;
  return null;
}

export function migrateLegacyStorageKeys(storage: Storage = window.localStorage): void {
  try {
    // Snapshot keys up front via the Storage contract (length + key(i)) — Object.keys
    // doesn't reliably enumerate Storage entries — because we mutate while migrating.
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k !== null) keys.push(k);
    }
    for (const key of keys) {
      const next = rebrandStorageKey(key);
      if (!next || next === key) continue;
      const value = storage.getItem(key);
      if (value !== null && storage.getItem(next) === null) {
        storage.setItem(next, value);
      }
      storage.removeItem(key);
    }
  } catch {
    // localStorage can be unavailable (private mode / disabled). Non-fatal.
  }
}
